import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { useAuth } from "../../contexts/auth-context";
import { useCreateCustomerSurveyAnswers } from "../../providers/customerSurvey";

import { useCustomerAddressesStorageData } from "../../providers/customerAddressesStorage";
import { IonModal, useIonAlert } from "@ionic/react";
import { SurveyAnswers } from "@core/types";
import ChoiceList from "../PollNewUserComponents/ChoiceList";
import DynamicFields from "../PollNewUserComponents/DynamicFields";
import SubmitButton from "../PollNewUserComponents/SubmitButton";
import { getAddressByIdBasedOnAuthStatus } from "../../utils/getAddressByIdBasedOnAuthStatus";
import { choicesConfig } from "../PollNewUserComponents/pollConfig";
import { useGetCustomerAddresses } from "../../providers/CustomerAddresses";
import { useGetCustomerSurveyModal } from "../../providers/customerSurvey/useGetCustomerSurveyModal";
import { queryClient } from "../../config/query-client";
import { getFullAddressForAddressType } from "../../utils";
import classNames from "classnames/bind";
import styles from "./PollNewUserModal.module.scss";
import { useGetProfileQuery, useUpdateProfileMutation } from "../../providers/profile";

const cx = classNames.bind(styles);

const setValueOptions = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: true,
};

const PollNewUser = () => {
  const history = useHistory();
  const { user, isUserAuthenticated } = useAuth();
  const fullName = user ? `${user.firstName} ${user.lastName}` : "";
  const { setCustomerSurveyModal, isCustomerSurveyModalOpen } = useGetCustomerSurveyModal();

  const { unAuthedCustomerAddresses, currentShippingAddressId } = useCustomerAddressesStorageData();

  const { mutate: updateProfile } = useUpdateProfileMutation();
  const { data: userData } = useGetProfileQuery({
    userId: user?.id ?? "",
    options: { enabled: !!user?.id },
  });

  const { data: customerAddresses } = useGetCustomerAddresses({
    customerId: user?.id,
    options: {
      enabled: !!user?.id,
    },
  });

  const customerAddress = getAddressByIdBasedOnAuthStatus(
    isUserAuthenticated!,
    currentShippingAddressId!,
    customerAddresses ?? [],
    unAuthedCustomerAddresses,
  );

  const fullAddress = customerAddress ? getFullAddressForAddressType(customerAddress) : "";

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isValid },
  } = useForm<SurveyAnswers>({
    mode: "onChange",
    defaultValues: {
      city: "",
      choice: [],
      position: "",
      brandName: "",
      storeName: "",
      companyName: "",
      fullName: fullName,
      deliveryAddress: fullAddress,
      establishmentName: "",
      establishmentType: "",
      establishmentCount: "",
      purchaseResponsible: "",
      multipleEstablishments: "",
      phoneNumber: user?.phone ?? "",
      establishmentCharacteristics: "",
    },
  });

  useEffect(() => {
    if (userData) {
      userData.phone && setValue("phoneNumber", userData.phone);

      const fullName = `${userData.firstName ?? ""} ${userData.lastName ?? ""}`;
      setValue("fullName", fullName, setValueOptions);
    }
  }, [userData]);

  useEffect(() => {
    if (currentShippingAddressId) {
      const customerAddress = getAddressByIdBasedOnAuthStatus(
        isUserAuthenticated!,
        currentShippingAddressId!,
        customerAddresses ?? [],
        unAuthedCustomerAddresses,
      );

      const fullAddress = customerAddress ? getFullAddressForAddressType(customerAddress) : "";

      setValue("deliveryAddress", fullAddress, setValueOptions);
      customerAddress?.city && setValue("city", customerAddress?.city, setValueOptions);
    }
  }, [currentShippingAddressId]);

  const handleDismissCustomerSurveyModal = () => {
    setCustomerSurveyModal(false);
  };

  const { mutate: createCustomerSurveyAnswers, isLoading } = useCreateCustomerSurveyAnswers();

  const handleSubmitPoll = (answers: SurveyAnswers) => {
    const choiceTexts = answers.choice.map(
      (choiceId) => choicesConfig.find((choiceConfig) => choiceConfig.id === choiceId)?.choice || "",
    );

    createCustomerSurveyAnswers(
      {
        answers: {
          ...answers,
          choice: choiceTexts,
        },
        user_phone: user?.phone!,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["customer-survey/get"]);
          handleDismissCustomerSurveyModal();

          const [firstName, lastName] = answers?.fullName?.split(" ");

          if ((firstName || lastName) && user?.id) {
            updateProfile({
              userId: user?.id,
              userPayload: {
                ...userData,
                firstName: firstName,
                lastName: lastName,
              },
            });
          }
          history.push("/home3");
        },
      },
    );
  };

  return (
    <IonModal
      backdropDismiss={false}
      isOpen={isCustomerSurveyModalOpen}
      onDidDismiss={handleDismissCustomerSurveyModal}
      className={cx("pollModal")}
    >
      <form className="flex flex-col gap-2 m-8 overflow-auto" onSubmit={handleSubmit(handleSubmitPoll)}>
        <ChoiceList control={control} />
        <DynamicFields control={control} watch={watch} />
        <SubmitButton isValid={isValid} isLoading={isLoading} />
      </form>
    </IonModal>
  );
};

export default PollNewUser;
